import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { notifyError } from '../app/notifications';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:3001';

// Create authenticated axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create non-authenticated axios instance for auth endpoints
export const authApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and active store header
apiClient.interceptors.request.use(
  (config) => {
    if (!config.headers) {
      config.headers = {} as any;
    }
    const token = localStorage.getItem('token');
    if (token) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    // Attach the active store ID so the backend knows which store the user is operating on
    const activeStoreId = localStorage.getItem('active-store-id');
    if (activeStoreId) {
      (config.headers as any)['X-Store-Id'] = activeStoreId;
    }
    const activeStoreTimezone = localStorage.getItem('store-timezone');
    if (activeStoreTimezone) {
      (config.headers as any)['X-Store-Timezone'] = activeStoreTimezone;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      notifyError('Session expired. Please login again.', 2000);
      // Redirect to login page
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      notifyError('You do not have permission to perform this action.', 2000);
    } else if (error.response && error.response.status >= 500) {
      notifyError('Server error. Please try again later.', 2000);
    }
    return Promise.reject(error);
  }
);

// Common API response interface
export interface ApiResponse<T = any> {
  status: boolean;
  message: string;
  data?: T;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  errors?: Record<string, string[]> | string;
  path?: string;
  method?: string;
  timestamp?: string;
}

// Wrapper function for safe API calls
export async function safeApi<T>(
  req: () => Promise<AxiosResponse<ApiResponse<T>>>
): Promise<ApiResponse<T>> {
  try {
    const response = await req();
    
    // Handle HTTP 204 No Content (successful delete operations)
    if (response.status === 204) {
      return {
        status: true,
        message: 'Operation completed successfully',
      } as ApiResponse<T>;
    }
    
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiResponse<T>>;
    
    if (axiosError.response?.data) {
      return axiosError.response.data;
    }
    
    // Fallback error response
    return {
      status: false,
      message: axiosError.message || 'Network error occurred',
      errors: axiosError.message,
    };
  }
}

export default apiClient;

// API functions for locations and suppliers
export const fetchLocationsAPI = () =>
  safeApi<string[]>(() =>
    apiClient.get<ApiResponse<string[]>>('/inventory/locations')
  );

// This function is used by stock features for dropdown data
export const fetchSuppliersForDropdownAPI = () =>
  safeApi<string[]>(() =>
    apiClient.get<ApiResponse<string[]>>('/suppliers/dropdown')
  );
